import {
  recordModelLoad,
  recordPrivacyDegraded,
  recordPrivacyDetectDisabled,
  recordPrivacyNoRoiRaw,
  recordPrivacySuccess,
} from "./metrics";
import {
  PrivacyDetectRequest,
  PrivacyDetectResponse,
  PrivacyWorkerRequest,
  PrivacyWorkerResponse,
} from "./privacyWorkerProtocol";
import {
  detectGlobalIntervalMs,
  PERSON_DETECTION_ENABLED,
  PRIVACY_PIPELINE_BUILD,
} from "./config";
import { Logger } from "../../../tools/Logger";

const TAG = "privacyWorkerClient";

/** Consecutive detector failures before a stream gives up on person boxes. */
const DETECT_FAILURE_LIMIT = 3;

export interface FrameResult {
  frameId: number;
  sourceWidth: number;
  sourceHeight: number;
  circle: { cx: number; cy: number; radius: number } | null;
  blurActive: boolean;
  hasBoxes: boolean;
}

interface StreamCallbacks {
  uavId: string;
  onFrame: (result: FrameResult) => void;
  onError: (message: string) => void;
}

interface StreamState {
  callbacks: StreamCallbacks;
  blurEnabled: boolean;
  detectEnabled: boolean;
  lastDetectAt: number;
  detectFailures: number;
  lastBlurActive: boolean;
  lastHasBoxes: boolean;
  lastCircle: FrameResult["circle"];
  /**
   * Frames handed to the blur worker vs frames it finished. The overlay being
   * visible only proves one frame completed, so these are what show whether a
   * feed is still moving or has degraded to a near-frozen blurred still.
   */
  framesIn: number;
  framesOut: number;
  /** Wall time of the last JPEG handed to the blur worker. */
  lastFrameAt: number;
}

/**
 * Owns the blur worker and the detect worker, and routes between them.
 *
 * Detection is a page-wide budget: one inference at a time, and a minimum
 * gap between inferences that stretches on 2-core machines. Commands and
 * telemetry live on the main thread; workers still share those two cores,
 * so ONNX must not run at 1 Hz per feed.
 */
class PrivacyWorkerClient {
  private worker: Worker | null = null;
  private detectWorker: Worker | null = null;
  private streams = new Map<string, StreamState>();
  private nextStreamId = 1;
  private workerBuild: string | null = null;
  private detectWorkerBuild: string | null = null;
  /** Last successful ONNX model_load from the detect worker. */
  private modelReady = false;
  private detectBusy = false;
  private lastDetectAt = 0;
  private lastDetectStreamId: string | null = null;

  register(
    uavId: string,
    canvas: OffscreenCanvas,
    options: {
      blurEnabled: boolean;
      personDetectionEnabled: boolean;
    },
    callbacks: StreamCallbacks
  ): string {
    const worker = this.ensureWorker();
    if (options.personDetectionEnabled && PERSON_DETECTION_ENABLED) {
      this.ensureDetectWorker();
    }
    const streamId = `${uavId}:${this.nextStreamId}`;
    this.nextStreamId += 1;
    this.streams.set(streamId, {
      callbacks,
      blurEnabled: options.blurEnabled,
      detectEnabled:
        options.personDetectionEnabled && PERSON_DETECTION_ENABLED,
      lastDetectAt: 0,
      detectFailures: 0,
      lastBlurActive: false,
      lastHasBoxes: false,
      lastCircle: null,
      framesIn: 0,
      framesOut: 0,
      lastFrameAt: 0,
    });
    const message: PrivacyWorkerRequest = {
      type: "register",
      streamId,
      uavId,
      canvas,
      blurEnabled: options.blurEnabled,
    };
    worker.postMessage(message, [canvas]);
    return streamId;
  }

  setBlurEnabled(streamId: string, blurEnabled: boolean): void {
    const stream = this.streams.get(streamId);
    if (!stream) {
      return;
    }
    stream.blurEnabled = blurEnabled;
    this.post({ type: "settings", streamId, blurEnabled });
    if (!blurEnabled) {
      // No blur ⇒ no person detect; drop sticky boxes immediately.
      stream.lastCircle = null;
      stream.lastBlurActive = false;
      this.clearStickyBoxes(streamId);
    }
  }

  setPersonDetectionEnabled(
    streamId: string,
    personDetectionEnabled: boolean
  ): void {
    const stream = this.streams.get(streamId);
    if (!stream) {
      return;
    }
    stream.detectEnabled =
      personDetectionEnabled && PERSON_DETECTION_ENABLED;
    stream.detectFailures = 0;
    if (stream.detectEnabled) {
      this.ensureDetectWorker();
      return;
    }
    this.clearStickyBoxes(streamId);
  }

  /** Drop held person boxes in the blur worker without changing detectEnabled. */
  private clearStickyBoxes(streamId: string): void {
    const stream = this.streams.get(streamId);
    if (!stream) {
      return;
    }
    stream.lastHasBoxes = false;
    this.post({ type: "settings", streamId, detectEnabled: false });
  }

  /**
   * Person detect only runs while we would blur: Video blur on + current cyan
   * ROI lock. No ring / blur off ⇒ skip ONNX and clear any leftover boxes.
   */
  private shouldRunPersonDetect(stream: StreamState): boolean {
    return (
      stream.detectEnabled &&
      stream.blurEnabled &&
      stream.lastCircle !== null
    );
  }

  /** Container size the canvas paints into, needed to convert the blur radius. */
  setContainerSize(
    streamId: string,
    containerWidth: number,
    containerHeight: number
  ): void {
    if (containerWidth > 0 && containerHeight > 0) {
      this.post({ type: "layout", streamId, containerWidth, containerHeight });
    }
  }

  enqueueFrame(streamId: string, frameId: number, blob: Blob): void {
    const stream = this.streams.get(streamId);
    if (stream) {
      stream.framesIn += 1;
      stream.lastFrameAt = Date.now();
    }
    this.post({ type: "frame", streamId, frameId, blob });
    this.maybeDetect(streamId, blob);
  }

  setPeek(streamId: string, enabled: boolean): void {
    this.post({ type: "peek", streamId, enabled });
  }

  unregister(streamId: string): void {
    if (!this.worker) {
      return;
    }
    this.post({ type: "unregister", streamId });
    this.streams.delete(streamId);
    if (this.streams.size === 0) {
      this.worker.terminate();
      this.worker = null;
      if (this.detectWorker) {
        this.detectWorker.terminate();
        this.detectWorker = null;
        this.detectWorkerBuild = null;
        this.modelReady = false;
        this.detectBusy = false;
      }
    }
  }

  getStatus(): {
    processors: number;
    detectLoop: boolean;
    modelReady: boolean;
    expectedBuild: string;
    workerBuild: string | null;
    detectWorkerBuild: string | null;
    workerCached: boolean;
    detectBusy: boolean;
    detectIntervalMs: number;
    frames: Array<{
      uavId: string;
      framesIn: number;
      framesOut: number;
      blurEnabled: boolean;
      detectEnabled: boolean;
      blurActive: boolean;
      hasBoxes: boolean;
      hasRoi: boolean;
    }>;
  } {
    const cached = (build: string | null) =>
      build !== null && build !== PRIVACY_PIPELINE_BUILD;
    return {
      processors: this.streams.size,
      detectLoop: this.detectWorker !== null,
      modelReady: this.modelReady,
      expectedBuild: PRIVACY_PIPELINE_BUILD,
      workerBuild: this.workerBuild,
      detectWorkerBuild: this.detectWorkerBuild,
      workerCached: cached(this.workerBuild) || cached(this.detectWorkerBuild),
      detectBusy: this.detectBusy,
      detectIntervalMs: detectGlobalIntervalMs(),
      frames: [...this.streams.values()].map((s) => ({
        uavId: s.callbacks.uavId,
        framesIn: s.framesIn,
        framesOut: s.framesOut,
        blurEnabled: s.blurEnabled,
        detectEnabled: s.detectEnabled,
        blurActive: s.lastBlurActive,
        hasBoxes: s.lastHasBoxes,
        hasRoi: s.lastCircle !== null,
      })),
    };
  }

  private maybeDetect(streamId: string, blob: Blob): void {
    if (this.detectBusy) {
      return;
    }
    if (typeof document !== "undefined" && document.hidden) {
      return;
    }
    const now = Date.now();
    if (now - this.lastDetectAt < detectGlobalIntervalMs()) {
      return;
    }
    const chosen = this.pickDetectStream(streamId);
    if (!chosen) {
      return;
    }
    const detectWorker = this.ensureDetectWorker();
    this.detectBusy = true;
    this.lastDetectAt = now;
    this.lastDetectStreamId = chosen;
    const stream = this.streams.get(chosen);
    if (stream) {
      stream.lastDetectAt = now;
    }
    const message: PrivacyDetectRequest = {
      type: "detect",
      streamId: chosen,
      blob,
    };
    detectWorker.postMessage(message);
  }

  /**
   * Round-robin the page-wide detect slot among feeds that should blur
   * (blur on + ROI lock) and have detect enabled. We only have this JPEG for
   * `frameStreamId`. Skip peers that have gone silent so they cannot hold the
   * slot forever.
   */
  private pickDetectStream(frameStreamId: string): string | null {
    const stream = this.streams.get(frameStreamId);
    if (!stream || !this.shouldRunPersonDetect(stream)) {
      return null;
    }
    const ids = Array.from(this.streams.keys()).filter((id) => {
      const s = this.streams.get(id);
      return !!s && this.shouldRunPersonDetect(s);
    });
    if (ids.length === 0) {
      return null;
    }
    const start = this.lastDetectStreamId
      ? ids.indexOf(this.lastDetectStreamId)
      : -1;
    const silentMs = Math.max(detectGlobalIntervalMs() * 2, 2000);
    const now = Date.now();
    for (let step = 1; step <= ids.length; step += 1) {
      const candidate = ids[(Math.max(start, -1) + step) % ids.length];
      if (candidate === frameStreamId) {
        return frameStreamId;
      }
      const peer = this.streams.get(candidate);
      const peerSilent =
        !peer || peer.lastFrameAt === 0 || now - peer.lastFrameAt > silentMs;
      if (!peerSilent) {
        // Next due feed is still producing — wait for its JPEG.
        return null;
      }
      // Peer is silent: keep walking so it cannot block the page-wide slot.
    }
    return null;
  }

  private ensureWorker(): Worker {
    if (this.worker) {
      return this.worker;
    }
    const worker = new Worker(new URL("./privacy.worker.ts", import.meta.url));
    worker.onmessage = (event: MessageEvent<PrivacyWorkerResponse>) => {
      this.handleMessage(event.data);
    };
    worker.onerror = (event: ErrorEvent) => {
      const message = event.message || "Privacy worker failed";
      this.streams.forEach((stream) => stream.callbacks.onError(message));
    };
    this.worker = worker;
    return worker;
  }

  private ensureDetectWorker(): Worker {
    if (this.detectWorker) {
      return this.detectWorker;
    }
    const worker = new Worker(
      new URL("./privacyDetect.worker.ts", import.meta.url)
    );
    worker.onmessage = (event: MessageEvent<PrivacyDetectResponse>) => {
      this.handleDetectMessage(event.data);
    };
    worker.onerror = (event: ErrorEvent) => {
      Logger.error(TAG, `detect worker failed: ${event.message}`);
      this.detectBusy = false;
      this.streams.forEach((_stream, streamId) => {
        this.noteDetectFailure(streamId, event.message);
      });
    };
    this.detectWorker = worker;
    return worker;
  }

  private post(message: PrivacyWorkerRequest): void {
    if (this.worker) {
      this.worker.postMessage(message);
    }
  }

  private handleMessage(message: PrivacyWorkerResponse): void {
    const stream = this.streams.get(message.streamId);
    if (!stream) {
      return;
    }
    if (message.type === "frame") {
      const prevCircle = stream.lastCircle;
      stream.framesOut += 1;
      stream.lastBlurActive = message.blurActive;
      stream.lastCircle = message.circle;
      stream.lastHasBoxes = message.hasBoxes;
      // ROI lost (or never locked this frame) ⇒ stop showing sticky boxes.
      if (!message.blurActive && (message.hasBoxes || prevCircle)) {
        this.clearStickyBoxes(message.streamId);
        stream.lastHasBoxes = false;
      }
      stream.callbacks.onFrame({
        frameId: message.frameId,
        sourceWidth: message.sourceWidth,
        sourceHeight: message.sourceHeight,
        circle: message.circle,
        blurActive: message.blurActive,
        hasBoxes: stream.lastHasBoxes,
      });
      if (message.blurActive) {
        recordPrivacySuccess(stream.callbacks.uavId, message.processMs);
      }
      return;
    }
    if (message.type === "error") {
      stream.callbacks.onError(message.message);
      if (stream.blurEnabled) {
        recordPrivacyDegraded(stream.callbacks.uavId, "worker_error", {
          message: message.message,
        });
      }
      return;
    }
    if (message.name === "build") {
      this.workerBuild = message.detail || "unknown";
      this.reportBuild("blur", this.workerBuild);
      return;
    }
    recordPrivacyNoRoiRaw(stream.callbacks.uavId);
  }

  private handleDetectMessage(message: PrivacyDetectResponse): void {
    if (message.type === "build") {
      this.detectWorkerBuild = message.detail || "unknown";
      this.reportBuild("detect", this.detectWorkerBuild);
      return;
    }
    if (message.type === "model_load") {
      this.modelReady = message.ok;
      recordModelLoad(message.ok, message.detail);
      return;
    }
    this.detectBusy = false;
    const stream = this.streams.get(message.streamId);
    if (!stream) {
      return;
    }
    if (message.type === "error") {
      this.noteDetectFailure(message.streamId, message.message);
      return;
    }
    // Detect may have been turned off, or ROI/blur dropped, while in flight.
    if (!this.shouldRunPersonDetect(stream)) {
      return;
    }
    stream.detectFailures = 0;
    this.post({
      type: "detections",
      streamId: message.streamId,
      detections: message.detections,
      sourceWidth: message.sourceWidth,
      sourceHeight: message.sourceHeight,
    });
  }

  private noteDetectFailure(streamId: string, detail?: string): void {
    const stream = this.streams.get(streamId);
    if (!stream || !stream.detectEnabled) {
      return;
    }
    stream.detectFailures += 1;
    if (stream.detectFailures >= DETECT_FAILURE_LIMIT) {
      stream.detectEnabled = false;
      stream.lastHasBoxes = false;
      this.post({ type: "settings", streamId, detectEnabled: false });
      recordPrivacyDetectDisabled(stream.callbacks.uavId, detail);
    }
  }

  private reportBuild(which: string, build: string): void {
    if (build === PRIVACY_PIPELINE_BUILD) {
      Logger.info(TAG, `${which} worker build ${build}`);
      return;
    }
    Logger.error(
      TAG,
      `${which} worker script is CACHED: worker=${build} expected=${PRIVACY_PIPELINE_BUILD}`
    );
  }
}

export const privacyWorkerClient = new PrivacyWorkerClient();

if (typeof window !== "undefined") {
  (window as any).__mannaPrivacyStatus = () => privacyWorkerClient.getStatus();
}
